/**
 * A coluna do meio: cabeçalho do canal + o que estiver lá dentro.
 *
 * Um canal de voz não tem mensagens — tem gente. Por isso o mesmo lugar mostra
 * o palco de vídeo quando o canal é de voz e a conversa quando é de texto: é a
 * mesma "sala", muda o que se faz nela.
 */

import type { Message } from "@greenvoice/shared";
import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useIsMobile, useIsPhone } from "../lib/media";
import { channelTitle, isTextual } from "../store/selectors";
import { useStore } from "../store/store";
import { FriendsView } from "./FriendsView";
import { IconHash, IconMembers, IconPin, IconSearch, IconSpeaker, IconThread } from "./Icons";
import { MenuButton } from "./MenuButton";
import { MessageComposer } from "./MessageComposer";
import { MessageList } from "./MessageList";
import { PinsPanel } from "./PinsPanel";
import { TOPBAR_SLOT_ID } from "./TopBar";
import { VoiceStage } from "./VoiceStage";

/**
 * O cabeçalho do canal — na barra de cima, quando ela existe.
 *
 * O pedido veio de um print da janela da chamada com a fila de cima rodeada a
 * vermelho: *"mesmo expandida fica pequena pq tem dois header (…) esse header
 * aqui ta sobrando, coloca todas essas infos no header principal"*. Estava
 * certo, e a conta é directa: dois cabeçalhos de 48px empilhados são 98px de
 * cromagem (com as linhas) antes de o vídeo começar. Um deles não pagava a
 * altura que ocupava.
 *
 * Não se apagou nada — mudou de sítio. O nome do canal, o tópico, a procura, as
 * fixadas e os membros continuam todos lá, à direita do nome do servidor, na
 * barra que já atravessava a app toda. E é a barra CERTA para eles: ela já era
 * a única que não desmonta ao trocar de vista, que é a razão de o botão
 * «Escritório» ter ido para lá.
 *
 * O destino é o `#topbar-slot` — o mesmo buraco que o escritório usa, e nunca
 * ao mesmo tempo que ele (as duas vistas excluem-se no `AppShell`). Num
 * TELEMÓVEL a barra de cima não existe de todo (`TopBar` devolve `null`: 48px
 * fixos numa app que é a conversa), e é por isso que a decisão aqui é feita
 * pela PRESENÇA do slot e não por uma media query própria: sem barra, o
 * cabeçalho fica onde sempre esteve, com o botão do menu que abre a gaveta —
 * que ali é a única porta para a lista de canais.
 */
function ChannelHead({ slot, children }: { slot: HTMLElement | null; children: ReactNode }) {
  if (slot) return createPortal(<div className="topbar__channel">{children}</div>, slot);
  return <header className="chat__head">{children}</header>;
}

export function ChatView() {
  const state = useStore();
  const channelId = state.activeChannelId;
  const channel = channelId ? state.channels[channelId] : undefined;
  const mobile = useIsMobile();

  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [pinsOpen, setPinsOpen] = useState(false);

  /**
   * O lugar do cabeçalho na barra de cima. Só existe depois de a barra estar no
   * DOM, e ela é IRMÃ deste componente — não antepassada —, por isso não há
   * `ref` que sirva: procura-se por id depois de montar, como o escritório já
   * faz para os controlos dele.
   *
   * Depende do `phone` porque a barra nasce e morre com ele (rodar um tablet
   * atravessa a fronteira): sem esta dependência, uma janela que deixa de ser
   * telemóvel ficava com o slot a `null` para sempre e com o cabeçalho preso em
   * baixo, por baixo de uma barra vazia.
   */
  const phone = useIsPhone();
  const [headSlot, setHeadSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHeadSlot(document.getElementById(TOPBAR_SLOT_ID));
  }, [phone]);

  /*
   * A porta para o escritório 2D já não está aqui.
   *
   * Esteve — uma pílula «Escritório» neste cabeçalho, gémea do botão «Chat» do
   * cabeçalho do escritório. O defeito era o par: cada metade vivia no
   * cabeçalho da SUA vista, e um cabeçalho morre com ela. Ao alternar, o botão
   * mudava de barra e de vizinhos, e o Kawã pediu o contrário — *"quero que
   * esse menu, seja fixo, tanto no discord quanto no office2d"*. Agora é UM
   * botão numa barra que não desmonta (`TopBar`), sempre no mesmo pixel, a
   * dizer sempre para onde se vai.
   *
   * O que fica neste cabeçalho é o que é do CANAL aberto: procurar, fixadas,
   * membros. Mudar de vista nunca foi disso.
   */

  // Mudar de canal limpa a resposta pendente: responder a uma mensagem de outro
  // canal enviaria para o sítio errado. O painel de fixadas fecha pela mesma
  // razão — é de outro canal.
  useEffect(() => {
    setReplyTo(null);
    setPinsOpen(false);
  }, [channelId]);

  // A procura é do SERVIDOR, não do canal: passar de #geral para #avisos não a
  // fecha (é o mesmo conjunto de resultados), mas trocar de servidor sim — os
  // resultados que lá estão são de canais onde já não se está.
  const closeSearch = state.closeSearch;
  useEffect(() => {
    closeSearch();
  }, [state.activeServerId, closeSearch]);

  // Fora de qualquer servidor e sem conversa aberta = a "casa": mostra os
  // amigos. Dentro de um servidor sem canal escolhido, fica o convite a escolher.
  if (!channel || !channelId) {
    if (state.activeServerId === null) return <FriendsView />;
    // O cabeçalho não é decoração: no telemóvel a barra de canais está fechada,
    // e sem ele este ecrã seria um convite a escolher um canal sem nenhuma
    // maneira de o fazer — a única saída da app seria recarregar a página.
    const server = state.servers[state.activeServerId];
    return (
      <section className="chat chat--empty">
        {/* Sem canal escolhido não há cabeçalho de canal nenhum, e o nome do
            servidor já está na barra de cima. Este cabeçalho existe só para o
            telemóvel, onde essa barra não há: sem ele, este ecrã seria um
            convite a escolher um canal sem nenhuma maneira de o fazer. */}
        {headSlot ? null : (
          <header className="chat__head">
            <MenuButton />
            <IconHash size={18} />
            <h1>{server?.name ?? "GreenVoice"}</h1>
          </header>
        )}
        <div className="chat__placeholder">
          <h2>GreenVoice</h2>
          <p>Escolha um canal {mobile ? "no menu" : "à esquerda"} para começar.</p>
        </div>
      </section>
    );
  }

  const voice = channel.type === "VOICE";
  /**
   * O canal de onde este tópico saiu.
   *
   * Um tópico está enfiado por baixo do pai na sidebar e o nome sozinho no topo
   * não diz de onde veio — sobretudo a quem lá chegou por um botão numa mensagem
   * e nunca passou pela lista. Daí o caminho de volta aqui, que é também a única
   * saída para quem abriu o ecrã já dentro do tópico.
   */
  const parent = channel.type === "THREAD" && channel.parentId ? state.channels[channel.parentId] : undefined;

  return (
    <section className="chat">
      <ChannelHead slot={headSlot}>
        <MenuButton />
        {voice ? (
          <IconSpeaker size={18} />
        ) : channel.type === "THREAD" ? (
          <IconThread size={18} />
        ) : (
          <IconHash size={18} />
        )}
        <h1>{channelTitle(state, channel)}</h1>
        {parent ? (
          <button
            type="button"
            className="chat__parent"
            title={`Voltar a #${parent.name}`}
            onClick={() => state.selectChannel(parent.id)}
          >
            em #{parent.name}
          </button>
        ) : null}
        {channel.topic ? (
          <>
            <span className="chat__divider" />
            <p className="chat__topic" title={channel.topic}>
              {channel.topic}
            </p>
          </>
        ) : null}

        <div className="chat__actions">
          {/* Procura-se no servidor inteiro, e por isso o botão também aparece
              num canal de voz: o que se quer encontrar raramente está no canal
              onde se está. Em DM não há — `activeServerId` nulo, e o endpoint é
              `/servers/:id/messages/search`. */}
          {channel.serverId ? (
            <button
              type="button"
              className={`icon-button${state.searchOpen ? " is-active" : ""}`}
              onClick={() => state.toggleSearch()}
              title="Procurar mensagens"
            >
              <IconSearch size={20} />
            </button>
          ) : null}
          {isTextual(channel) ? (
            <button
              type="button"
              className={`icon-button${pinsOpen ? " is-active" : ""}`}
              onClick={() => setPinsOpen((v) => !v)}
              title="Mensagens fixadas"
            >
              <IconPin size={20} />
            </button>
          ) : null}
          {/* Só num ecrã estreito, onde a coluna dos membros é uma GAVETA e este
              botão é a única maneira de a abrir — como o hamburguer é para a da
              esquerda. No computador a coluna arruma-se pela seta que vive nela
              (ver MembersCollapse, em MemberList): cada seta na sua coluna, a
              apontar para onde a manda. Um botão numa barra partilhada não
              dizia de que coluna estava a falar. */}
          {mobile && channel.serverId ? (
            <button
              type="button"
              className={`icon-button${state.memberListOpen ? " is-active" : ""}`}
              onClick={() => state.toggleMemberList()}
              title={state.memberListOpen ? "Esconder membros" : "Mostrar membros"}
            >
              <IconMembers size={20} />
            </button>
          ) : null}
        </div>
      </ChannelHead>

      {/* No telemóvel as ferramentas do palco ficam no palco: a barra de cima já
          leva o cabeçalho do canal e a porta, e não há largura para mais. */}
      {voice ? (
        <VoiceStage channelId={channelId} toolsInBar={headSlot !== null && !phone} />
      ) : isTextual(channel) ? (
        <>
          <MessageList channelId={channelId} onReply={setReplyTo} />
          <MessageComposer channelId={channelId} replyTo={replyTo} onCancelReply={() => setReplyTo(null)} />
        </>
      ) : null}

      {pinsOpen ? (
        <PinsPanel channelId={channelId} serverId={channel.serverId} onClose={() => setPinsOpen(false)} />
      ) : null}
    </section>
  );
}
